/**
 * Checkout Session Storage utilities
 * 
 * Standard SaaS: Centralized management of checkout state in sessionStorage.
 * Used to track pending checkouts across page navigations (pricing → signup/login → Stripe).
 */

const STORAGE_KEYS = {
  priceId: 'leadsme:checkout:priceId',
  type: 'leadsme:checkout:type',
  billingType: 'leadsme:checkout:billingType',
  purchaseEventId: 'leadsme:checkout:purchaseEventId',
  redirected: 'leadsme:checkout:redirected',
  timestamp: 'leadsme:checkout:timestamp',
} as const;

const PORTAL_KEYS = {
  redirected: 'leadsme:portal:redirected',
  timestamp: 'leadsme:portal:timestamp',
} as const;

// Checkout return validity window (10 minutes). Used for browser "back" returns from Stripe.
const CHECKOUT_RETURN_VALIDITY_MS = 10 * 60 * 1000;

export type CheckoutType = 'monthly' | 'yearly';

export interface CheckoutState {
  priceId: string | null;
  type: CheckoutType | null;
  redirected: boolean;
  timestamp: number | null;
  isRecentReturn: boolean;
}

/**
 * Get current checkout state from sessionStorage
 */
export function getCheckoutState(): CheckoutState {
  const priceId = sessionStorage.getItem(STORAGE_KEYS.priceId);
  const type = sessionStorage.getItem(STORAGE_KEYS.type) as CheckoutType | null;
  const redirected = sessionStorage.getItem(STORAGE_KEYS.redirected) === 'true';
  const timestampStr = sessionStorage.getItem(STORAGE_KEYS.timestamp);
  const timestamp = timestampStr ? parseInt(timestampStr, 10) : null;
  const isRecentReturn =
    redirected &&
    timestamp !== null &&
    Date.now() - timestamp < CHECKOUT_RETURN_VALIDITY_MS;

  return { priceId, type, redirected, timestamp, isRecentReturn };
}

/**
 * Check if there's a pending checkout to process
 */
export function hasPendingCheckout(): boolean {
  const { priceId, type } = getCheckoutState();
  return Boolean(priceId && type);
}

/**
 * Flag that we started a Stripe redirect.
 * Used to detect "browser back" returns and treat them like a cancellation.
 */
export function markRedirectedToStripe(): void {
  sessionStorage.setItem(STORAGE_KEYS.redirected, 'true');
  sessionStorage.setItem(STORAGE_KEYS.timestamp, Date.now().toString());
}

/**
 * True when the user likely returned from Stripe via browser back (not via cancel_url).
 */
export function isReturningFromStripe(): boolean {
  const { isRecentReturn } = getCheckoutState();
  return isRecentReturn;
}

/**
 * Set pending checkout (called from PricingPage before redirecting to auth)
 */
export function setPendingCheckout(priceId: string, type: CheckoutType): void {
  sessionStorage.setItem(STORAGE_KEYS.priceId, priceId);
  sessionStorage.setItem(STORAGE_KEYS.type, type);
}

/** Persists plan type through Stripe redirect for conversion tracking on return. */
export function setCheckoutBillingType(type: CheckoutType): void {
  sessionStorage.setItem(STORAGE_KEYS.billingType, type);
}

export function getCheckoutBillingType(): CheckoutType | null {
  const type = sessionStorage.getItem(STORAGE_KEYS.billingType);
  return type === 'monthly' || type === 'yearly' ? type : null;
}

export function setCheckoutPurchaseEventId(eventId: string): void {
  sessionStorage.setItem(STORAGE_KEYS.purchaseEventId, eventId);
}

export function getCheckoutPurchaseEventId(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.purchaseEventId);
}

/**
 * Clear only the checkout intent (priceId/type) while keeping redirected/timestamp flags.
 * Standard SaaS: after starting the Stripe redirect, we don't want to re-trigger checkout on auth pages,
 * but we keep redirected/timestamp to detect back button returns.
 */
export function clearPendingCheckout(): void {
  sessionStorage.removeItem(STORAGE_KEYS.priceId);
  sessionStorage.removeItem(STORAGE_KEYS.type);
}

/**
 * Clear only Stripe-return detection flags.
 */
export function clearStripeRedirectState(): void {
  sessionStorage.removeItem(STORAGE_KEYS.redirected);
  sessionStorage.removeItem(STORAGE_KEYS.timestamp);
}

/**
 * Clear all checkout state
 */
export function clearCheckoutState(): void {
  sessionStorage.removeItem(STORAGE_KEYS.priceId);
  sessionStorage.removeItem(STORAGE_KEYS.type);
  sessionStorage.removeItem(STORAGE_KEYS.billingType);
  sessionStorage.removeItem(STORAGE_KEYS.purchaseEventId);
  sessionStorage.removeItem(STORAGE_KEYS.redirected);
  sessionStorage.removeItem(STORAGE_KEYS.timestamp);
}

/**
 * Clear portal state
 */
export function clearPortalState(): void {
  sessionStorage.removeItem(PORTAL_KEYS.redirected);
  sessionStorage.removeItem(PORTAL_KEYS.timestamp);
}

/**
 * Check if user is returning from billing portal
 */
export function isReturningFromPortal(): boolean {
  const redirected = sessionStorage.getItem(PORTAL_KEYS.redirected) === 'true';
  const timestampStr = sessionStorage.getItem(PORTAL_KEYS.timestamp);
  const timestamp = timestampStr ? parseInt(timestampStr, 10) : null;
  // 10 minutes
  const isRecent = timestamp !== null && (Date.now() - timestamp) < 10 * 60 * 1000;
  
  return redirected && (isRecent || timestamp === null);
}

/**
 * Mark that we've redirected to billing portal
 */
export function markRedirectedToPortal(): void {
  sessionStorage.setItem(PORTAL_KEYS.redirected, 'true');
  sessionStorage.setItem(PORTAL_KEYS.timestamp, Date.now().toString());
}

/**
 * Check if the portal redirect flag is set (for cleanup of recent or stale flags)
 */
export function hasPortalRedirectFlag(): boolean {
  return sessionStorage.getItem(PORTAL_KEYS.redirected) === 'true';
}
